import React, { useState } from 'react';
import cn from 'classnames';

import { ROOM_STATUS, USER_ROLES } from 'constants/index';
import { useAppDispatch, useAppSelector, useTranslation, useGAEventCommonProps } from 'client/hooks';
import { selectRoomStatus, selectRoomId } from 'client/redux/room/selectors';
import { selectIsShowShares } from 'client/redux/ui/selectors';
import { selectIsLoggedIn, selectUserName } from 'client/redux/client/selectors';
import { selectIsBroadcasting } from 'client/redux/mux-details/selectors';
import {
  selectIsCurrentUserBlocked,
  selectOccupancy,
  selectRaiseHandUsersTotal,
  selectSignedUsersOccupancy,
} from 'client/redux/pubnub/selectors';
import { roomInfoLS, analytics } from 'client/utils';
import { setIsShowFooterPopUpOverlay } from 'client/redux/ui';
import Hints, { HintItemType } from 'client/components/common/Hints';
import TransitionComponent from 'client/components/common/TransitionComponent';

import SharesActions from '../../../SharesActions';
import EmotionsActions from '../../../EmotionsActions';
import UsersStatisticsButton from '../../../Drawers/UsersStatisticsDrawer/UsersStatisticButton';
import UsersStatisticMenu from '../../../Drawers/UsersStatisticsDrawer/UsersStatisticMenu';
import ListenerRequestActions from '../../../ListenerRequestActions';
import PopUpToLogin from './PopUpToLogin';

import css from './ListenerActionsPanel.module.scss';

interface Props {
  className?: string;
}

const ListenerActionsPanel: React.FC<Props> = (props) => {
  const { className = '' } = props;

  const dispatch = useAppDispatch();
  const [isShowStatisticDrawer, setIsShowStatisticDrawer] = useState<boolean>(false);
  const [isShowToLoginPopUp, setIsShowToLoginPopUp] = useState<boolean>(false);
  const [toLoginPopUpText, setToLoginPopUpText] = useState<'emotions' | 'speaking' | ''>('');

  const isShowShares = useAppSelector(selectIsShowShares);
  const isBlocked = useAppSelector(selectIsCurrentUserBlocked);
  const pendingRequestsCount = useAppSelector(selectRaiseHandUsersTotal);
  const anonymousListenersCount = useAppSelector(selectOccupancy);
  const signedListenersCount = useAppSelector(selectSignedUsersOccupancy);
  const totalListenersCount = anonymousListenersCount + signedListenersCount;
  const isUserLoggedIn = useAppSelector(selectIsLoggedIn);
  const isBroadcasting = useAppSelector(selectIsBroadcasting);
  const roomStatus = useAppSelector(selectRoomStatus);
  const userName = useAppSelector(selectUserName);
  const [isClosedHint, setIsClosedHint] = useState<boolean>(true);
  const { translate: translateHints } = useTranslation('hints');
  const { translate: translateActions } = useTranslation('actionsPanel');
  const roomId = useAppSelector(selectRoomId);
  const { common } = useGAEventCommonProps();
  const isUsersStatisticsInactive = React.useMemo(() => {
    if (
      roomStatus === ROOM_STATUS.INACTIVE ||
      roomStatus === ROOM_STATUS.FINISHED ||
      roomStatus === ROOM_STATUS.CANCELLED
    )
      return true;

    return false;
  }, [roomStatus]);

  const isActionsInactive = React.useMemo(() => {
    if (roomStatus === ROOM_STATUS.INACTIVE || !isBroadcasting || isBlocked) return true;

    return false;
  }, [roomStatus, isBroadcasting, isBlocked]);

  const statisticDrawerState = (state: boolean) => {
    if (state) {
      analytics.gtag.event('audio_chat_content_click', {
        ...common,
        text_clicked: translateActions('btns.usersStatisticBtn'),
      });
    }
    setIsShowStatisticDrawer(state);
  };

  const closeToLoginPopUp = () => {
    setIsShowToLoginPopUp(false);
    setToLoginPopUpText('');
  };

  const showToLoginPopUp = (text: 'emotions' | 'speaking') => {
    if (text === 'emotions') {
      analytics.gtag.event('audio_chat_content_click', {
        ...common,
        text_clicked: translateActions('btns.emojiesBtn'),
      });
    } else {
      analytics.gtag.event('audio_chat_content_click', {
        ...common,
        text_clicked: translateActions('btns.handBtnState'),
      });
    }

    setToLoginPopUpText(text);
    setIsShowToLoginPopUp(true);
  };

  const onHintClose = () => {
    setIsClosedHint(true);
    dispatch(setIsShowFooterPopUpOverlay(false));
  };

  const isShowWelcomeHint = roomStatus === ROOM_STATUS.ACTIVE && isUserLoggedIn && !isBroadcasting;

  React.useEffect(() => {
    if (isShowWelcomeHint) {
      dispatch(setIsShowFooterPopUpOverlay(true));
      setIsClosedHint(false);
    }
  }, [dispatch, isShowWelcomeHint]);

  React.useEffect(() => {
    const lsRoomInfo = roomInfoLS.getData(roomId);
    const isListenerConnectedToCoversation = lsRoomInfo?.isListenerConnectedToCoversation;
    if (isBlocked && !isListenerConnectedToCoversation) {
      roomInfoLS.setData(roomId, { isListenerConnectedToCoversation: true });
    }
  }, [isBlocked]);

  const hints = React.useMemo<HintItemType[]>(
    () =>
      isShowWelcomeHint
        ? [
            {
              icon: 'red_hand',
              title: translateHints(`${USER_ROLES.LISTENER}.initialHint.title`, { name: userName }),
              text: translateHints(`${USER_ROLES.LISTENER}.initialHint.text`),
            },
          ]
        : [],
    [translateHints, isShowWelcomeHint, userName],
  );

  return (
    <div className={cn(css.actionBtnsWrapper, className)} data-testid="listener-actions-panel">
      <div className={css.actionBtnsWrapperI}>
        <div
          data-testid="shares-actions-wrapper"
          className={cn(css.action, css.shares, isShowShares && css.biggestIndex)}
        >
          <SharesActions />
        </div>
        <div
          className={cn(css.action, css.userActions, isActionsInactive && css.inactive)}
          data-testid="action-mic-hand"
        >
          <ListenerRequestActions isAnonymous={!isUserLoggedIn} showToLoginPopUp={showToLoginPopUp} />
        </div>
        <div data-testid="action-emojies" className={cn(css.action, css.emojies, isActionsInactive && css.inactive)}>
          <EmotionsActions isAnonymous={!isUserLoggedIn} showToLoginPopUp={showToLoginPopUp} />
        </div>
        <div
          className={cn(css.action, css.usersStatistics, isUsersStatisticsInactive && css.inactive)}
          data-testid="action-userstatistis"
        >
          <UsersStatisticsButton statisticDrawerState={statisticDrawerState} />
        </div>
      </div>

      <UsersStatisticMenu
        visible={isShowStatisticDrawer}
        totalListenersCount={totalListenersCount}
        connectedListenersCount={signedListenersCount}
        pendingRequestsCount={pendingRequestsCount}
      />

      {hints && (
        <TransitionComponent type="opacity" visible={!isClosedHint}>
          <Hints hints={hints} next={0} handleClose={onHintClose} />
        </TransitionComponent>
      )}

      {isShowToLoginPopUp && (
        <PopUpToLogin
          className={isShowToLoginPopUp ? 'active' : ''}
          closePopup={closeToLoginPopUp}
          toLoginPopUpText={toLoginPopUpText}
        />
      )}
    </div>
  );
};

export default React.memo(ListenerActionsPanel);
